import React, { useState, useEffect } from 'react';
import api from '../services/api';

function Templates() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [newTemplate, setNewTemplate] = useState({
    name: '',
    description: '',
    category: 'general',
    command: '',
    target_os: 'all'
  });

  const categories = [
    { value: 'general', label: '📦 General', color: '#888' },
    { value: 'recon', label: '🔍 Reconnaissance', color: '#00aaff' },
    { value: 'persistence', label: '🔗 Persistence', color: '#ff4444' },
    { value: 'exfil', label: '📤 Exfiltration', color: '#ffaa00' },
    { value: 'lateral', label: '↔️ Lateral Movement', color: '#ff66ff' },
    { value: 'cleanup', label: '🧹 Cleanup', color: '#00ff88' },
  ];

  useEffect(() => {
    fetchTemplates();
  }, [selectedCategory]);

  const fetchTemplates = async () => {
    try {
      const params = selectedCategory !== 'all' ? { category: selectedCategory } : {};
      const response = await api.get('/api/templates', { params });
      setTemplates(response.data.templates || []);
    } catch (error) {
      console.error('Failed to fetch templates:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!newTemplate.name || !newTemplate.command) return;
    
    try {
      await api.post('/api/templates', newTemplate);
      setShowCreate(false);
      setNewTemplate({
        name: '',
        description: '',
        category: 'general',
        command: '',
        target_os: 'all'
      });
      fetchTemplates();
    } catch (error) {
      console.error('Failed to create template:', error);
    }
  };

  const handleDelete = async (templateId) => {
    if (!window.confirm('Delete this template?')) return;
    
    try {
      await api.delete(`/api/templates/${templateId}`);
      fetchTemplates();
    } catch (error) {
      console.error('Failed to delete template:', error);
    }
  };

  const getCategoryInfo = (cat) => {
    return categories.find(c => c.value === cat) || categories[0];
  };

  return (
    <div className="page-container">
      <div className="page-header" style={{ marginBottom: '30px' }}>
        <div>
          <h1 className="page-title">📋 Command Templates</h1>
          <p className="page-subtitle">Save and reuse common commands</p>
        </div>
        <button className="btn btn-primary" onClick={() => setShowCreate(true)}>
          + New Template
        </button>
      </div>

      <div className="category-filter" style={{ marginBottom: '30px' }}>
        <button
          className={`category-btn ${selectedCategory === 'all' ? 'active' : ''}`}
          onClick={() => setSelectedCategory('all')}
        >
          All
        </button>
        {categories.map(cat => (
          <button
            key={cat.value}
            className={`category-btn ${selectedCategory === cat.value ? 'active' : ''}`}
            onClick={() => setSelectedCategory(cat.value)}
            style={{ '--cat-color': cat.color }}
          >
            {cat.label}
          </button>
        ))}
      </div>

      <div className="templates-grid">
        {loading ? (
          <div className="loading-spinner">Loading...</div>
        ) : templates.length === 0 ? (
          <div className="empty-state">
            <p>No templates yet. Create one to get started!</p>
          </div>
        ) : (
          templates.map(template => {
            const catInfo = getCategoryInfo(template.category);
            return (
              <div key={template.template_id} className="template-card">
                <div className="template-header">
                  <span className="template-category" style={{ background: catInfo.color }}>
                    {catInfo.label}
                  </span>
                  <span className="template-os">{template.target_os}</span>
                </div>
                <h3 className="template-name">{template.name}</h3>
                {template.description && (
                  <p className="template-desc">{template.description}</p>
                )}
                <pre className="template-command">{template.command}</pre>
                <div className="template-footer">
                  <span className="template-uses">Used {template.use_count} times</span>
                  <div className="template-actions">
                    <button
                      className="btn btn-sm btn-ghost"
                      onClick={() => navigator.clipboard.writeText(template.command)}
                    >
                      Copy
                    </button>
                    <button
                      className="btn btn-sm btn-danger"
                      onClick={() => handleDelete(template.template_id)}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      {showCreate && (
        <div className="modal-overlay" onClick={() => setShowCreate(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <h2>Create Template</h2>
            
            <div className="form-group">
              <label>Name *</label>
              <input
                type="text"
                className="form-control"
                value={newTemplate.name}
                onChange={e => setNewTemplate({...newTemplate, name: e.target.value})}
                placeholder="e.g., List Users"
              />
            </div>

            <div className="form-group">
              <label>Description</label>
              <input
                type="text"
                className="form-control"
                value={newTemplate.description}
                onChange={e => setNewTemplate({...newTemplate, description: e.target.value})}
                placeholder="What does this command do?"
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Category</label>
                <select
                  className="form-select"
                  value={newTemplate.category}
                  onChange={e => setNewTemplate({...newTemplate, category: e.target.value})}
                >
                  {categories.map(cat => (
                    <option key={cat.value} value={cat.value}>{cat.label}</option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>Target OS</label>
                <select
                  className="form-select"
                  value={newTemplate.target_os}
                  onChange={e => setNewTemplate({...newTemplate, target_os: e.target.value})}
                >
                  <option value="all">All</option>
                  <option value="windows">Windows</option>
                  <option value="linux">Linux</option>
                  <option value="darwin">macOS</option>
                </select>
              </div>
            </div>

            <div className="form-group">
              <label>Command *</label>
              <textarea
                className="form-control"
                rows={5}
                value={newTemplate.command}
                onChange={e => setNewTemplate({...newTemplate, command: e.target.value})}
                placeholder="Enter command(s)..."
                style={{ fontFamily: 'monospace' }}
              />
            </div>

            <div className="modal-actions">
              <button className="btn btn-ghost" onClick={() => setShowCreate(false)}>
                Cancel
              </button>
              <button className="btn btn-primary" onClick={handleCreate}>
                Create Template
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        .category-filter {
          display: flex;
          gap: 12px;
          flex-wrap: wrap;
        }

        .category-btn {
          padding: 10px 20px;
          border: 1px solid var(--border-color);
          background: var(--bg-secondary);
          color: var(--text-secondary);
          border-radius: 20px;
          cursor: pointer;
          transition: all 0.2s;
          font-size: 14px;
        }

        .category-btn:hover {
          background: var(--bg-hover);
          color: var(--text-primary);
        }

        .category-btn.active {
          background: var(--neon-green);
          color: var(--bg-primary);
          border-color: var(--neon-green);
        }

        .templates-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
          gap: 20px;
        }

        .template-card {
          background: var(--bg-secondary);
          border: 1px solid var(--border-color);
          border-radius: 8px;
          padding: 20px;
          transition: all 0.2s;
        }

        .template-card:hover {
          border-color: var(--neon-green);
          box-shadow: 0 0 10px rgba(255, 34, 68, 0.2);
        }

        .template-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 12px;
        }

        .template-category {
          font-size: 11px;
          padding: 3px 8px;
          border-radius: 10px;
          color: white;
          font-weight: 600;
        }

        .template-os {
          font-size: 11px;
          color: var(--text-muted);
          text-transform: uppercase;
        }

        .template-name {
          margin: 0 0 8px 0;
          color: var(--text-primary);
          font-size: 16px;
        }

        .template-desc {
          color: var(--text-secondary);
          font-size: 13px;
          margin: 0 0 12px 0;
        }

        .template-command {
          background: var(--bg-primary);
          padding: 12px;
          border-radius: 6px;
          font-size: 12px;
          overflow-x: auto;
          color: var(--neon-green);
          margin: 0 0 12px 0;
          max-height: 120px;
        }

        .template-footer {
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .template-uses {
          font-size: 12px;
          color: var(--text-muted);
        }

        .template-actions {
          display: flex;
          gap: 8px;
        }

        .form-row {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 15px;
        }

        .empty-state {
          grid-column: 1 / -1;
          text-align: center;
          padding: 60px;
          color: var(--text-muted);
        }
      `}</style>
    </div>
  );
}

export default Templates;
